import { useState, useEffect, useRef } from 'react';
import { X, RefreshCw, Download, AlertTriangle, CheckCircle, Clock, Loader2, RotateCcw } from 'lucide-react';

const STATE_MAP = {
    JOB_STATE_PENDING: {
        label: 'Pending',
        description: 'Batch is queued and waiting to be processed',
        icon: Clock,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        ringColor: 'ring-amber-400',
        pulseClass: 'animate-pulse'
    },
    JOB_STATE_RUNNING: {
        label: 'Running',
        description: 'Batch is currently being processed by Gemini',
        icon: Loader2,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        ringColor: 'ring-blue-400',
        pulseClass: 'animate-spin'
    },
    JOB_STATE_SUCCEEDED: {
        label: 'Completed',
        description: 'Batch processing is complete! Results are ready.',
        icon: CheckCircle,
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        ringColor: 'ring-emerald-400',
        pulseClass: ''
    },
    JOB_STATE_FAILED: {
        label: 'Failed',
        description: 'Batch processing failed. You can retry the batch.',
        icon: AlertTriangle,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        ringColor: 'ring-red-400',
        pulseClass: ''
    },
    JOB_STATE_CANCELLED: {
        label: 'Cancelled',
        description: 'Batch was cancelled by the user.',
        icon: X,
        color: 'text-slate-600',
        bgColor: 'bg-slate-50',
        borderColor: 'border-slate-200',
        ringColor: 'ring-slate-400',
        pulseClass: ''
    },
    JOB_STATE_EXPIRED: {
        label: 'Expired',
        description: 'Batch expired after running too long (48h limit).',
        icon: AlertTriangle,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        ringColor: 'ring-orange-400',
        pulseClass: ''
    }
};

const UNKNOWN_STATE = {
    label: 'Unknown',
    description: 'Unable to determine batch status',
    icon: Clock,
    color: 'text-slate-500',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    ringColor: 'ring-slate-300',
    pulseClass: ''
};

function BatchStatusModal({
    isOpen,
    onClose,
    batchJobName,
    onCheckStatus,
    onFetchResults,
    onRetry,
    statusData,
    isLoading,
    isFetchingResults
}) {
    const [autoRefresh, setAutoRefresh] = useState(false);
    const intervalRef = useRef(null);

    // Auto-refresh polling
    useEffect(() => {
        if (autoRefresh && isOpen && batchJobName) {
            intervalRef.current = setInterval(() => {
                onCheckStatus?.();
            }, 15000); // 15-second poll
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [autoRefresh, isOpen, batchJobName, onCheckStatus]);

    // Stop auto-refresh once job is complete
    useEffect(() => {
        const completedStates = ['JOB_STATE_SUCCEEDED', 'JOB_STATE_FAILED', 'JOB_STATE_CANCELLED', 'JOB_STATE_EXPIRED'];
        if (statusData?.state && completedStates.includes(statusData.state)) {
            setAutoRefresh(false);
        }
    }, [statusData?.state]);

    if (!isOpen) return null;

    const stateInfo = STATE_MAP[statusData?.state] || UNKNOWN_STATE;
    const StateIcon = stateInfo.icon;
    const isComplete = statusData?.state === 'JOB_STATE_SUCCEEDED';
    const isFailed = statusData?.state === 'JOB_STATE_FAILED';
    const isTerminal = ['JOB_STATE_SUCCEEDED', 'JOB_STATE_FAILED', 'JOB_STATE_CANCELLED', 'JOB_STATE_EXPIRED'].includes(statusData?.state);

    // Extract batch ID for display
    const displayId = batchJobName ? batchJobName.replace('batches/', '') : 'N/A';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-lg mx-4 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 overflow-hidden">
                {/* Header gradient bar */}
                <div className={`h-1.5 w-full ${isComplete ? 'bg-gradient-to-r from-emerald-400 to-teal-400' : isFailed ? 'bg-gradient-to-r from-red-400 to-rose-400' : 'bg-gradient-to-r from-blue-400 to-indigo-400'}`} />

                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-5 pb-2">
                    <h2 className="text-xl font-bold text-slate-900">Batch Job Status</h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 pb-6">
                    {/* Batch ID */}
                    <div className="mb-4 px-4 py-2.5 bg-slate-50 rounded-xl border border-slate-200">
                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Batch ID</span>
                        <p className="text-sm font-mono text-slate-800 mt-0.5 break-all">{displayId}</p>
                    </div>

                    {/* Status Card */}
                    {statusData ? (
                        <div className={`mb-5 p-5 rounded-2xl border-2 ${stateInfo.borderColor} ${stateInfo.bgColor} transition-all`}>
                            <div className="flex items-center gap-3 mb-2">
                                <div className={`p-2.5 rounded-xl bg-white/80 shadow-sm ring-2 ${stateInfo.ringColor}`}>
                                    <StateIcon className={`w-6 h-6 ${stateInfo.color} ${stateInfo.pulseClass}`} />
                                </div>
                                <div>
                                    <h3 className={`text-lg font-bold ${stateInfo.color}`}>
                                        {stateInfo.label}
                                    </h3>
                                    <p className="text-sm text-slate-600">{stateInfo.description}</p>
                                </div>
                            </div>

                            {/* Error details */}
                            {statusData.error && (
                                <div className="mt-3 p-3 bg-red-100/60 rounded-xl border border-red-200">
                                    <p className="text-sm text-red-700 font-medium">Error Details:</p>
                                    <p className="text-xs text-red-600 mt-1 break-all">{statusData.error}</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="mb-5 p-5 rounded-2xl border-2 border-slate-200 bg-slate-50 text-center">
                            <p className="text-slate-500 text-sm">Click "Check Status" to get the latest batch status</p>
                        </div>
                    )}

                    {/* Auto-refresh toggle (only when not terminal) */}
                    {!isTerminal && (
                        <div className="mb-5 flex items-center justify-between px-4 py-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                            <div className="flex items-center gap-2">
                                <RefreshCw className={`w-4 h-4 text-indigo-500 ${autoRefresh ? 'animate-spin' : ''}`} />
                                <span className="text-sm font-medium text-indigo-700">Auto-refresh every 15s</span>
                            </div>
                            <button
                                onClick={() => setAutoRefresh(!autoRefresh)}
                                className={`relative w-11 h-6 rounded-full transition-colors ${autoRefresh ? 'bg-indigo-500' : 'bg-slate-300'}`}
                            >
                                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${autoRefresh ? 'translate-x-5' : ''}`} />
                            </button>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2.5">
                        {/* Check Status */}
                        <button
                            onClick={onCheckStatus}
                            disabled={isLoading}
                            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                            {isLoading ? 'Checking...' : 'Check Status'}
                        </button>

                        {/* Fetch Results (only when succeeded) */}
                        {isComplete && (
                            <button
                                onClick={onFetchResults}
                                disabled={isFetchingResults}
                                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-200"
                            >
                                <Download className={`w-4 h-4 ${isFetchingResults ? 'animate-bounce' : ''}`} />
                                {isFetchingResults ? 'Fetching Results...' : 'Fetch Results'}
                            </button>
                        )}

                        {/* Retry (only when failed) */}
                        {isFailed && onRetry && (
                            <button
                                onClick={onRetry}
                                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors shadow-lg shadow-red-200"
                            >
                                <RotateCcw className="w-4 h-4" />
                                Retry Batch
                            </button>
                        )}

                        {/* Close */}
                        <button
                            onClick={onClose}
                            className="w-full px-4 py-2.5 text-slate-600 rounded-xl font-medium hover:bg-slate-100 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BatchStatusModal;
